package viewer

import editor.structure.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.html.dom.append
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.url.URLSearchParams
import rendering.CameraManager
import rendering.EffectManager
import rendering.RendererManager
import rendering.SceneManager
import three.OrbitControls
import three.js.Group
import three.js.Vector3
import util.DebugManager
import viewer.ViewerManager.structure

// todo some kind of "table" plane to
//  make the structure stand on something
// todo "reset pan" button (top right?)
/**
 * ## Viewer
 *
 * Simple 3D viewer for a model. Similar to the editor in that you can navigate around the model, but without the
 * ability to edit it.
 */
object ViewerManager {
    /**
     * The group of THREE objects that are currently in the viewer.
     */
    private val objects = Group().apply {
        add(*defaultStructure.instantiate())
    }

    /**
     * The group of THREE objects that are directly added to the scene.
     */
    private val group = Group().apply {
        add(objects)
    }

    /**
     * An abstraction layer for the current structure that is being viewed.
     *
     * Consists of [StructureElement]s.
     */
    private val structure: MutableStructure = defaultStructure.toMutableList()

    /**
     * Set the current structure to the specified [structure].
     */
    private fun setStructure(structure: Structure) {
        this.structure.clear()
        this.structure.addAll(structure)
        onStructureUpdate()
    }

    /**
     * Allow the user to zoom and rotate the camera.
     *
     * Event though this is not used in the viewer, we still need to instantiate it here to get the functionality!
     */
    @Suppress("unused")
    private val orbitControls = OrbitControls(CameraManager.camera, RendererManager.renderer.domElement)

    private var selectedElt: StructureElement? = null

    /**
     * Called once at the beginning to initialize the editor.
     * - add the html
     * - add the group which contains the structure
     * - add mousedown/-up listeners for switching between states
     */
    suspend fun init() {
        document.body!!.append {
            ViewerInstructions.addHtmlTo(this)
        }

        val params = URLSearchParams(document.location!!.search)
        params.get("structure")?.let {
            val data = window.fetch("/structures/$it.timberix.json").await().text().await()
            val structure = Json.decodeFromString<Structure>(data)
            setStructure(structure)
        }
        SceneManager.scene.add(group)
        EffectManager.init()

        // for some reason it works on document but doesn't work on window.
        // adding true as an argument for capture = true also doesn't work.
        document.addEventListener("click", {
            if (it.target !is HTMLCanvasElement) return@addEventListener
            selectedElt = ViewerSelection.handleSelection(objects, structure)

            label?.remove()
            selectedElt?.let { elt ->
                label = ViewerLabel.createLabel(elt)?.firstOrNull() as? HTMLDivElement
                println("new label: $label")
            }
        })
    }

    private var tmpVec = Vector3()
    private var label: HTMLDivElement? = null

    /**
     * Called every frame to render the current structure, and additionally some kind of preview,
     * depending on the mouse position and state.
     */
    fun render() {
        DebugManager.debug("HI!!")

        // information overlay for selected brick
//        selectedElt?.let { selectedElt ->
//            selectedElt.object3D.getWorldPosition(tmpVec)
//            tmpVec.y += 0.5 // a bit above
//
//            // 2. project to NDC
//            tmpVec.project(CameraManager.camera)
//
//            // 3. convert NDC (-1..1) to screen coords
//            val x = ((tmpVec.x.toDouble() * 0.5) + 0.5) * RendererManager.renderer.domElement.clientWidth
//            val y = (-tmpVec.y.toDouble() * 0.5 + 0.5) * RendererManager.renderer.domElement.clientHeight
//
//            // 4. move label
//            label?.style?.left = "${x}px"
//            label?.style?.top = "${y}px"
//        }

        DebugManager.render()
        RendererManager.render()
    }

    /**
     * Update the three-js objects according to the current [structure]. Center the structure.
     */
    private fun onStructureUpdate() {
        objects.clear()
        objects.add(*structure.instantiate())

        // clone all materials to fix multiple materials changing color when selected!
        // unclear why this is necessary as materials should already be cloned in StructureElement::object3D
        objects.traverse { obj ->
            val o = obj.asDynamic()
            if (o.isMesh == true && o.material != null) {
                o.material = o.material.clone()
            }
        }

        // Center the structure
        group.position.set(0, 0, 0)
        group.position.sub(structure.center)
    }
}